import axios from "axios";
import {
  CSRF_HEADER,
  applyCsrfHeader,
  csrfTokenFromCookie,
  getCsrfToken,
  isCsrfError,
  rememberCsrfToken,
} from "../utils/csrf";
// DEV-89 (`oq-ММБ-121` (а)): исходный адрес при уходе ко входу — чтобы вернуть
// человека туда же после входа.
import { rememberReturnPath } from "../utils/returnPath";
// `DEV-95.1`: одна запись об ошибке запроса — метод, маршрут, код,
// `X-Request-Id`, текст, время; без тел и заголовков.
import { logApiError } from "../utils/logApiError";

export const API_URL = process.env.REACT_APP_API_URL;

const $api = axios.create({
  withCredentials: true,
  baseURL: API_URL,
});

// Один общий промис на время refresh: параллельные 401 ждут одно продление.
let refreshPromise: Promise<boolean> | null = null;

// БЕЗ-01/БЕЗ-05 (DEV-55). Токен доступа живёт только в httpOnly-cookie, которую
// браузер прикладывает сам; сценарию страницы он недоступен, поэтому заголовка
// Authorization здесь нет вовсе. Изменяющие запросы несут анти-CSRF-признак.
$api.interceptors.request.use(
  (config) => applyCsrfHeader(config),
  (error) => Promise.reject(error),
);

function rememberFromResponse(response: any): void {
  rememberCsrfToken(response?.data?.csrfToken);
}

$api.interceptors.response.use(
  (response) => {
    rememberFromResponse(response);
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};

    // `DEV-88` (`OPS-20` (в)): тело ответа в консоль не пишем. Прежний
    // `console.log("Error data", …)` выкладывал его в консоль браузера — в том
    // числе тому, кто просто открыл инструменты разработчика.

    const url = String(originalRequest.url || "");
    // Продление имеет смысл только для защищённого запроса: у /refresh
    // продлевать нечего, а отказ /login — это неверные данные, не истёкшая
    // сессия. Иначе неудачный вход уходил бы в цикл продлений.
    const isRefreshCall = url.includes("/refresh");
    const isLoginCall = url.includes("/login");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshCall &&
      !isLoginCall
    ) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshResponse = await axios.get(`${API_URL}/refresh`, {
              withCredentials: true,
            });

            rememberFromResponse(refreshResponse);

            return true;
          } catch (refreshError: any) {
            console.error("[API_RESPONSE] Не удалось продлить сессию", {
              status: refreshError.response?.status,
            });

            // Сессия кончилась — возвращаем человека ко входу.
            if (typeof window !== "undefined") {
              // DEV-89 (`oq-ММБ-121` (а)): запоминаем, где человека застало
              // истечение сессии, — путь вместе с параметрами. После входа
              // обработчик входа вернёт его сюда, а не на страницу по роли.
              rememberReturnPath(
                `${window.location.pathname}${window.location.search}`,
              );
              window.location.href = "/";
            }

            return false;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      const refreshed = await refreshPromise;

      if (!refreshed) {
        return Promise.reject(error);
      }

      // Повторяем исходный запрос: cookie уйдёт сама, признак берём актуальный
      // (после продления сервер перевыпускает и его).
      const csrf = getCsrfToken();

      if (csrf) {
        if (typeof originalRequest.headers?.set === "function") {
          originalRequest.headers.set(CSRF_HEADER, csrf);
        } else {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            [CSRF_HEADER]: csrf,
          };
        }
      }

      return axios(originalRequest);
    }

    // DEV-85 (ТЕСТ-Н32): 403 с причиной CSRF — признак разошёлся с cookie
    // (например, сессию продлили в другой вкладке, и cookie перевыпущена).
    // Один повтор с признаком, заново прочитанным из cookie; серверная проверка
    // при этом не ослабляется. Если cookie странице не видна (другой домен),
    // повторять нечем — отказ уходит наверх как прежде.
    if (isCsrfError(error) && !originalRequest._csrfRetry) {
      const fresh = csrfTokenFromCookie();

      if (fresh) {
        originalRequest._csrfRetry = true;
        applyCsrfHeader(originalRequest);
        return axios(originalRequest);
      }
    }

    if (error.response?.status >= 500) {
      // `DEV-95`/`DEV-95.1`: запись о 5xx — одной функцией (`utils/logApiError`).
      // Тело ответа и тело запроса в журнал не попадают: в них бывают и
      // персональные данные, и секреты; для разбора остаются метод, маршрут, код,
      // время и идентификатор запроса.
      logApiError("[API_RESPONSE] Server error", error);
    }

    return Promise.reject(error);
  },
);

export default $api;
